import {
  recipeSchema,
  recipeWriteInputSchema,
  type ExportJob,
  type RecipeWriteInput,
} from '@recipes/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Skeleton,
} from '@recipes/ui';
import { format } from 'date-fns';
import { de as deLocale, enUS } from 'date-fns/locale';
import { Download, FileUp, Trash2 } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router';
import { z } from 'zod';
import { toast } from '@/components/feedback/toaster';
import { PageHeader } from '@/components/layout/page-header';
import { PasswordField } from '@/features/profile/password-field';
import { recipeToWriteInput } from '@/features/recipes/recipe-to-input';
import { api } from '@/lib/api';
import { IdentityAccountLink, useAuthMode } from '@/features/auth/identity';
import { useAuth } from '@/lib/auth/auth-provider';
import { describeError, toastError } from '@/lib/errors';
import { currentLocale } from '@/lib/i18n';
import { queryKeys } from '@/lib/query-keys';

const STATUS_VARIANT: Record<
  ExportJob['status'],
  'default' | 'success' | 'destructive' | 'muted' | 'warning'
> = {
  QUEUED: 'muted',
  RUNNING: 'warning',
  READY: 'success',
  FAILED: 'destructive',
  EXPIRED: 'muted',
};

/** Accept our own export format (recipes-manager JSON) or a bare list of write inputs. */
export function parseOwnJson(text: string): RecipeWriteInput[] {
  const raw: unknown = JSON.parse(text);
  const ownExport = z
    .object({ format: z.literal('recipes-manager'), recipes: z.array(recipeSchema) })
    .safeParse(raw);
  if (ownExport.success) return ownExport.data.recipes.map(recipeToWriteInput);
  const list = Array.isArray(raw) ? raw : (raw as { recipes?: unknown })?.recipes;
  const inputs = z.array(recipeWriteInputSchema).safeParse(list);
  if (inputs.success) return inputs.data;
  throw new Error('INVALID_FORMAT');
}

function ExportsSection() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const dateLocale = currentLocale() === 'de' ? deLocale : enUS;
  const [includeImages, setIncludeImages] = useState(true);
  const exportsQuery = useQuery({
    queryKey: queryKeys.exports,
    queryFn: () => api.dataExport.list(),
    refetchInterval: (query) =>
      query.state.data?.some((j) => j.status === 'QUEUED' || j.status === 'RUNNING') ? 5000 : false,
  });
  const create = useMutation({
    mutationFn: () => api.dataExport.create({ includeImages }),
    onSuccess: () => {
      toast.success(t('profile.data.exportRequested'));
      void queryClient.invalidateQueries({ queryKey: queryKeys.exports });
    },
    onError: (e) => toastError(e),
  });
  return (
    <section aria-labelledby="export-heading" className="space-y-3">
      <h2 id="export-heading" className="text-lg font-semibold">
        {t('profile.data.export')}
      </h2>
      <p className="text-sm text-muted-foreground">{t('profile.data.exportDescription')}</p>
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <Checkbox
            id="export-images"
            checked={includeImages}
            onCheckedChange={(v) => setIncludeImages(v === true)}
          />
          <Label htmlFor="export-images">{t('profile.data.includeImages')}</Label>
        </div>
        <Button
          onClick={() => create.mutate()}
          loading={create.isPending}
          data-testid="request-export"
        >
          <Download aria-hidden /> {t('profile.data.requestExport')}
        </Button>
      </div>
      {exportsQuery.isPending ? (
        <Skeleton className="h-16" />
      ) : exportsQuery.isError ? (
        <Alert variant="destructive">{describeError(exportsQuery.error)}</Alert>
      ) : exportsQuery.data.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('profile.data.noExports')}</p>
      ) : (
        <ul className="divide-y rounded-lg border" data-testid="export-list">
          {exportsQuery.data.map((job) => (
            <li key={job.id} className="flex flex-wrap items-center gap-3 p-3 text-sm">
              <Badge variant={STATUS_VARIANT[job.status]}>
                {t(`profile.data.status.${job.status}`)}
              </Badge>
              <span className="text-muted-foreground">
                {format(new Date(job.createdAt), 'PPp', { locale: dateLocale })}
              </span>
              {job.sizeBytes !== null && (
                <span className="text-muted-foreground">
                  {(job.sizeBytes / 1024 / 1024).toFixed(1)} MB
                </span>
              )}
              {job.expiresAt && job.status === 'READY' && (
                <span className="text-muted-foreground">
                  {t('profile.data.expires')}:{' '}
                  {format(new Date(job.expiresAt), 'PP', { locale: dateLocale })}
                </span>
              )}
              {job.downloadUrl && job.status === 'READY' && (
                <Button asChild size="sm" variant="outline" className="ml-auto">
                  <a href={job.downloadUrl} download>
                    <Download aria-hidden /> {t('profile.data.download')}
                  </a>
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

function ImportSection() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [skipDuplicates, setSkipDuplicates] = useState(true);
  const [result, setResult] = useState<{ created: number; skipped: number } | null>(null);
  const importJson = useMutation({
    mutationFn: async (file: File) => {
      const recipes = parseOwnJson(await file.text());
      return api.imports.ownJson({ recipes, skipDuplicates });
    },
    onSuccess: (res) => {
      setResult(res);
      void queryClient.invalidateQueries({ queryKey: queryKeys.recipes.all });
    },
    onError: (e) => {
      if (e instanceof Error && e.message === 'INVALID_FORMAT')
        toast.error(t('profile.data.importInvalid'));
      else toastError(e, t('profile.data.importFailed'));
    },
  });
  return (
    <section aria-labelledby="import-heading" className="space-y-3">
      <h2 id="import-heading" className="text-lg font-semibold">
        {t('profile.data.import')}
      </h2>
      <p className="text-sm text-muted-foreground">{t('profile.data.importDescription')}</p>
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2">
          <Checkbox
            id="import-skip"
            checked={skipDuplicates}
            onCheckedChange={(v) => setSkipDuplicates(v === true)}
          />
          <Label htmlFor="import-skip">{t('profile.data.skipDuplicates')}</Label>
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="application/json,.json"
          className="sr-only"
          aria-label={t('profile.data.chooseFile')}
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) importJson.mutate(file);
            e.target.value = '';
          }}
          data-testid="import-json-input"
        />
        <Button
          variant="outline"
          onClick={() => fileRef.current?.click()}
          loading={importJson.isPending}
        >
          <FileUp aria-hidden /> {t('profile.data.chooseFile')}
        </Button>
      </div>
      {result && (
        <Alert variant="success" data-testid="import-result">
          {t('profile.data.importResult', { created: result.created, skipped: result.skipped })}
        </Alert>
      )}
    </section>
  );
}

function DeleteAccountSection() {
  const { t } = useTranslation();
  const authMode = useAuthMode();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const remove = useMutation({
    mutationFn: () => api.users.deleteAccount({ password, confirmation: 'DELETE' }),
    onSuccess: async () => {
      toast.success(t('profile.data.deleted'));
      await logout().catch(() => undefined);
      navigate('/login', { replace: true });
    },
    onError: (e) => toastError(e),
  });
  return (
    <section
      aria-labelledby="delete-heading"
      className="space-y-3 rounded-lg border border-destructive/40 p-4"
    >
      <h2 id="delete-heading" className="text-lg font-semibold text-destructive">
        {t('profile.data.deleteAccount')}
      </h2>
      {authMode.mode === 'oidc' && authMode.identity ? (
        <>
          <p className="text-sm text-muted-foreground">{t('auth.identity.deleteAt')}</p>
          <IdentityAccountLink identity={authMode.identity} testId="delete-account-identity" />
        </>
      ) : (
        <>
          <p className="text-sm text-muted-foreground">{t('profile.data.deleteDescription')}</p>
          <Button variant="destructive" onClick={() => setOpen(true)} data-testid="delete-account">
            <Trash2 aria-hidden /> {t('profile.data.deleteAccount')}
          </Button>
        </>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent closeLabel={t('common.close')} data-testid="delete-account-dialog">
          <DialogHeader>
            <DialogTitle>{t('profile.data.deleteAccount')}</DialogTitle>
            <DialogDescription>{t('profile.data.deleteConfirmDescription')}</DialogDescription>
          </DialogHeader>
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (confirmation === 'DELETE' && password) remove.mutate();
            }}
          >
            <div>
              <Label htmlFor="delete-password" className="mb-1 block">
                {t('auth.password')}
              </Label>
              <PasswordField
                id="delete-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
              />
            </div>
            <div>
              <Label htmlFor="delete-confirmation" className="mb-1 block">
                {t('profile.data.typeDelete')}
              </Label>
              <Input
                id="delete-confirmation"
                value={confirmation}
                onChange={(e) => setConfirmation(e.target.value)}
                autoComplete="off"
                data-testid="delete-confirmation"
              />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                variant="destructive"
                disabled={confirmation !== 'DELETE' || !password}
                loading={remove.isPending}
                data-testid="delete-account-confirm"
              >
                {t('profile.data.deleteForever')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </section>
  );
}

export function DataPage() {
  const { t } = useTranslation();
  return (
    <div className="mx-auto max-w-2xl space-y-8 p-4 md:p-6" data-testid="data-page">
      <PageHeader
        eyebrow={
          <Link to="/profile" className="underline-offset-4 hover:underline">
            ← {t('profile.title')}
          </Link>
        }
        title={t('profile.data.title')}
      />
      <ExportsSection />
      <ImportSection />
      <DeleteAccountSection />
    </div>
  );
}
